import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_USERS_TABLE } from "../../../constants/FirestoreConstant";
import { message } from "antd";

// Create context
const UseCmsUsers = createContext();

// Provider component
export const CmsUsersProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [cmsUsers, setCmsUsers] = useState([]);
  const [error, setError] = useState(null);
  const [listener, setListener] = useState(null);

  // Set up listener when the provider mounts
  useEffect(() => {
    setupListener();

    // Cleanup listener when unmounting
    return () => {
      if (listener) {
        listener();
      }
    };
  }, []);

  const setupListener = () => {
    setIsLoading(true);
    setError(null);

    try {
      // Query for admin and superAdmin users
      const cmsUsersQuery = query(
        collection(firestore, FIRESTORE_USERS_TABLE),
        where("userRole", "in", ["admin", "superAdmin"]),
      );

      const unsubscribe = onSnapshot(
        cmsUsersQuery,
        (snapshot) => {
          try {
            const usersData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setCmsUsers(usersData);
            setError(null);
            setIsLoading(false);
          } catch (err) {
            console.error("Error processing CMS users snapshot:", err);
            setError(err.message);
            setIsLoading(false);
            message.error("Failed to process CMS users data");
          }
        },
        (error) => {
          console.error("Error listening to CMS users:", error);
          setError(error.message);
          setIsLoading(false);
          message.error("Failed to fetch CMS users data");
        },
      );

      setListener(() => unsubscribe);
    } catch (error) {
      console.error("Error setting up CMS users listener:", error);
      setError(error.message);
      setIsLoading(false);
      message.error("Failed to setup CMS users listener");
    }
  };

  // Helper functions
  const getUserById = (id) => {
    return cmsUsers.find((user) => user.id === id || user.userId === id);
  };

  const getUsersByRole = (role) => {
    return cmsUsers.filter((user) => user.userRole === role);
  };

  const getUsersByRegion = (region) => {
    return cmsUsers.filter((user) => user.userRegion === region || user.userRegion === "all");
  };

  const searchUsers = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return cmsUsers.filter(
      (user) =>
        user.userFirstName?.toLowerCase().includes(term) ||
        user.userLastName?.toLowerCase().includes(term) ||
        user.userEmail?.toLowerCase().includes(term) ||
        user.userId?.toLowerCase().includes(term),
    );
  };

  const getAdminUsers = () => {
    return cmsUsers.filter((user) => user.userRole === "admin");
  };

  const getSuperAdminUsers = () => {
    return cmsUsers.filter((user) => user.userRole === "superAdmin");
  };

  return (
    <UseCmsUsers.Provider
      value={{
        isLoading,
        cmsUsers,
        error,
        getUserById,
        getUsersByRole,
        getUsersByRegion,
        searchUsers,
        getAdminUsers,
        getSuperAdminUsers,
      }}
    >
      {children}
    </UseCmsUsers.Provider>
  );
};

// Custom hook to use the CMS users context
export const useCmsUsers = () => {
  const context = useContext(UseCmsUsers);

  if (context === undefined) {
    throw new Error("useCmsUsers must be used within a CmsUsersProvider");
  }

  return context;
};
